/**
 * [OFFLINE_UPLOAD_QUEUE_PHASE_3] Upload API Utility
 * 
 * Centralized upload function for photos, signatures, and other files.
 * Compatible with Supabase Storage and existing upload endpoints.
 */

import { dbg } from './dbg';

export interface UploadOptions {
  onProgress?: (progress: number) => void;
  signal?: AbortSignal;
}

/**
 * Upload a file to the specified endpoint using FormData
 * 
 * @param endpoint - API endpoint (e.g., '/api/mobile/upload-photo')
 * @param formData - FormData containing file and metadata
 * @param options - Optional upload configuration
 * @returns Response data from server
 */
export async function apiUploadFile(
  endpoint: string,
  formData: FormData,
  options: UploadOptions = {}
): Promise<any> {
  try {
    dbg.log('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Starting upload:', endpoint);

    const response = await fetch(endpoint, {
      method: 'POST',
      body: formData,
      credentials: 'omit', // Matches existing upload pattern
      signal: options.signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed (${response.status}): ${errorText}`);
    }

    const data = await response.json();
    dbg.log('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Upload successful:', endpoint);
    
    return data;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      dbg.log('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Upload aborted:', endpoint);
      throw new Error('Upload cancelled');
    }
    
    dbg.error('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Upload failed:', endpoint, error);
    throw error;
  }
}

/**
 * Convert a File or Blob to base64 string for storage
 */
export async function fileToBase64(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Convert base64 string back to File object
 */
export async function base64ToFile(
  base64: string,
  filename: string,
  mimeType: string
): Promise<File> {
  const response = await fetch(base64);
  const blob = await response.blob();
  return new File([blob], filename, { type: mimeType });
}

